/**
 * The account, from the tab's side: who is signed in, how to sign in and
 * out, and the progress record kept on the server for a signed-in learner.
 * The Worker's half is worker/account.ts; the shapes are docs/contracts.md,
 * "Accounts".
 *
 * Signing in is optional and never in the way. The game works exactly as
 * before for a learner who never touches it; an account adds progress that
 * follows the person across browsers and a chat identity the room vouches
 * for, and that is all this file is about.
 *
 * Signing in opens a window, not a redirect. The page owns a running Linux,
 * and a redirect to Google would throw it away mid-level (App.tsx explains
 * why a level change is a pushState for the same reason). So the sign-in
 * happens in a popup, and the popup reports back through a BroadcastChannel
 * — which is per origin, not per window, and so is not cut by the page's
 * cross-origin-opener policy the way window.opener is — and closes itself.
 * A browser that blocks the popup gets the redirect instead: a lost VM is
 * better than no way in.
 */
import type { Progress } from '../progress';
import { getLocale } from './locale';

export interface AccountUser {
  id: string;
  name: string;
  email: string | null;
  picture: string | null;
  /** The operator on the account, or null while this person has never built one (lib/operator.ts). */
  avatarSeed: string | null;
  /**
   * Whether a Google account is attached. False for an account opened with an
   * emailed code, which has no Google name to go back to. Read as true when a
   * Worker older than the field leaves it out, since every account was then.
   */
  google: boolean;
  /**
   * A guest account: made with one click and no address, for the free levels
   * on the server workspace (worker/account.ts `createGuest`). It cannot buy
   * or redeem; signing in makes it a real account and keeps its progress.
   * False when a Worker older than the field leaves it out.
   */
  guest: boolean;
}

/** What the sign-in window offers besides Google and an email code. */
export interface SignInOptions {
  /** Offer "Continue as a guest": only where the server workspace is what needs the account. */
  guest?: boolean;
}

export interface AccountState {
  /** False until the Worker has Google configured, and when it cannot be reached. */
  enabled: boolean;
  user: AccountUser | null;
  /**
   * Whether this deployment takes pictures in the chat.
   *
   * Not an account fact, and answered by the account route because that route
   * is already the one question the tab asks about how this deployment is
   * configured (worker/account.ts says the same at more length). False when
   * the Worker cannot be reached, which is the same default every other flag
   * here takes: a panel that offers nothing is better than one that offers
   * something and refuses it.
   */
  pictures: boolean;
}

/** What the popup posts when it is done: signed in, or why not. */
export type AuthMessage = { t: 'signed-in' } | { t: 'failed'; reason: string };

/** The channel the popup speaks on. The Worker's page names the same string. */
export const AUTH_CHANNEL = 'engineeros-auth';

const ME = '/api/auth/me';
const PROGRESS = '/api/progress';
const AVATAR = '/api/avatar';
const NAME = '/api/name';

export async function fetchAccount(): Promise<AccountState> {
  try {
    const r = await fetch(ME, { credentials: 'same-origin', cache: 'no-store' });
    if (!r.ok) return { enabled: false, user: null, pictures: false };
    const body = (await r.json()) as Partial<AccountState>;
    const u = body.user;
    const user: AccountUser | null =
      u && typeof u.id === 'string' && typeof u.name === 'string'
        ? {
            id: u.id,
            name: u.name,
            email: typeof u.email === 'string' ? u.email : null,
            picture: typeof u.picture === 'string' ? u.picture : null,
            avatarSeed: typeof u.avatarSeed === 'string' ? u.avatarSeed : null,
            google: u.google !== false,
            guest: u.guest === true,
          }
        : null;
    return { enabled: body.enabled === true, user, pictures: body.pictures === true };
  } catch {
    return { enabled: false, user: null, pictures: false };
  }
}

/**
 * Where the sign-in window starts. The Worker answers with a page offering
 * Google or a code by email, in the language this tab is in, or — on a
 * deployment with no email sender — with a redirect straight to Google. Either
 * way it ends on the same channel, so nothing here knows which was taken.
 */
export function signInUrl(popup: boolean, options: SignInOptions = {}): string {
  const lang = `lang=${encodeURIComponent(getLocale())}${options.guest ? '&guest=1' : ''}`;
  return popup ? `/api/auth/start?popup=1&${lang}` : `/api/auth/start?${lang}`;
}

/**
 * Open the sign-in window. Returns false when the browser refused to open
 * one, in which case the caller falls back to a redirect. Named, so a second
 * click focuses the window already open rather than opening another.
 */
export function openSignIn(options: SignInOptions = {}): boolean {
  try {
    const w = window.open(signInUrl(true, options), 'engineeros-signin', 'popup=yes,width=480,height=640,noopener=no');
    return w !== null;
  } catch {
    return false;
  }
}

export async function signOut(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } catch {
    /* the cookie may survive a lost request; the next load will show it, and the button is still there */
  }
}

/**
 * What came back from asking for the account to be deleted.
 *
 * Three answers and not a boolean, because the middle one is not a failure and
 * must not be shown as one. `entitled` is the server saying the account has
 * bought the course and this request did not say it knew that — the screen's
 * cue to put the forfeit sentence in front of the learner and ask again, which
 * is the one thing a `false` could not have told it to do.
 */
export type DeleteOutcome = 'gone' | 'entitled' | 'failed';

/**
 * Close the account. Irreversible, and the whole of the tab's half of it.
 *
 * `userId` is sent rather than assumed, because the Worker refuses a request
 * that names an account its cookie does not belong to — the case where this
 * tab has been open since before somebody signed in as somebody else in
 * another one. That is a refusal worth having on a route that deletes things:
 * every other route in this file would merely write to the wrong record.
 *
 * `forfeit` is the second press, and it is false until the server has said
 * there is something to forfeit. Sending it unasked would mean a client that
 * pre-agreed to give up a purchase on the learner's behalf, which is the one
 * thing the warning exists to stop.
 *
 * Anything that is not a clean answer is `failed`, including a refusal this
 * build does not have a word for: the account is still there in that case, and
 * saying so is the honest outcome. The signed-out state that follows a
 * success is not set here — the caller owns it, because it is the same state
 * signing out sets and there must be one place that does it.
 */
export async function deleteAccount(userId: string, forfeit: boolean): Promise<DeleteOutcome> {
  try {
    const r = await fetch('/api/auth/account', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, confirm: true, forfeit }),
    });
    if (r.ok) return 'gone';
    const body = (await r.json().catch(() => null)) as { entitled?: unknown } | null;
    return r.status === 409 && body?.entitled === true ? 'entitled' : 'failed';
  } catch {
    return 'failed';
  }
}

export interface RemoteProgress {
  progress: Progress | null;
  updatedAt: number | null;
  epoch: number;
  userId: string;
}
export interface ProgressIdentity { epoch: number; userId: string }
function remoteProgress(value: unknown): RemoteProgress | undefined {
  const v = value as Partial<RemoteProgress> | null;
  return v && typeof v.userId === 'string' && Number.isSafeInteger(v.epoch) && v.epoch! >= 0
    && (v.progress === null || v.progress?.version === 2) ? v as RemoteProgress : undefined;
}
export async function pullProgress(signal?: AbortSignal): Promise<RemoteProgress | undefined> {
  try {
    const r = await fetch(PROGRESS, { credentials: 'same-origin', cache: 'no-store', signal });
    return r.ok ? remoteProgress(await r.json()) : undefined;
  } catch { return undefined; }
}
async function writeProgress(method: string, body: unknown, signal?: AbortSignal):
  Promise<{ status: number; snapshot?: RemoteProgress } | undefined> {
  try {
    const r = await fetch(PROGRESS, { method, credentials: 'same-origin', signal,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return { status: r.status, snapshot: remoteProgress(await r.json()) };
  } catch { return undefined; }
}
export const pushProgress = (progress: Progress, identity: ProgressIdentity, signal?: AbortSignal) =>
  writeProgress('PUT', { progress, ...identity }, signal);

/**
 * Wear this operator on the account, or null to wear none.
 *
 * False when it did not land, which the caller reports without taking the
 * figure away: the browser has it either way, and the next sign-in reconciles
 * the two (lib/operator.ts). One route, no GET — what the account is wearing
 * arrives with the account itself.
 */
export async function pushAvatar(seed: string | null): Promise<boolean> {
  try {
    const r = await fetch(AVATAR, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seed }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * Be called this on the account, or null to go back to the name the account
 * came with — Google's, or "Learner" for an account opened with a code.
 *
 * Returns the name the account shows afterwards, which for null is Google's
 * and is the one thing the tab cannot work out for itself: the chosen name has
 * been standing in front of it since the page loaded. Null on any refusal or
 * any failure — the caller has a name on screen that did not change, and
 * saying so is better than showing a name the server does not hold.
 *
 * Unlike the operator, nothing about this is optimistic and nothing is kept in
 * this browser for a signed-in learner. A name is what other people call them,
 * so the copy that matters is the one the rooms read on the upgrade, and a tab
 * that showed a rename the server never took would be lying about what
 * everybody else can see.
 */
export async function pushName(name: string | null): Promise<string | null> {
  try {
    const r = await fetch(NAME, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!r.ok) return null;
    const body = (await r.json()) as { name?: unknown };
    return typeof body.name === 'string' ? body.name : null;
  } catch {
    return null;
  }
}

/** Two initials for a person with no picture, or when the picture does not load. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
